import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom'; // useParams로 변경
import SellerLayout from './SellerLayout';

const SellerProductOption = () => {
  const navigate = useNavigate();
  const { product_id } = useParams(); // Route에 정의한 :product_id 이름 그대로 가져옴
  const productId = product_id; // 가독성을 위해 매핑

  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [optionGroups, setOptionGroups] = useState([]);

  // 새 옵션 그룹 등록을 위한 State
  const [newGroup, setNewGroup] = useState({
    group_name: '',
    required: 'N',
    max_select: 1,
    sort_order: 0,
    items: [{ item_name: '', extra_price: 0, sort_order: 0 }]
  });

  // 데이터 불러오기 (GET)
  const fetchData = async () => {
    if (!productId) {
      alert('상품 ID가 올바르지 않습니다.');
      navigate(-1);
      return;
    }
    try {
      // Nginx 접두사 생략 규격 반영 (/api 제외된 라우트 매핑 타겟)
      const res = await axios.get(`/api/products/${productId}/options`, {
        withCredentials: true,
      });

      if (res.data && res.data.success) {
        // 백엔드 구조에 맞춰 상단 상점 정보 및 옵션 그룹 매핑
        setShopName(res.data.shop_name || '셀러 가맹점');
        setOptionGroups(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        alert(res.data?.message || '옵션 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error || err?.response?.data?.message;

      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      alert(error || '옵션 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [productId, navigate]);

  // 하위 옵션 아이템 동적 행 추가
  const handleAddItemRow = () => {
    setNewGroup(prev => ({
      ...prev,
      items: [...prev.items, { item_name: '', extra_price: 0, sort_order: prev.items.length }]
    }));
  };

  // 하위 옵션 아이템 동적 행 제거
  const handleRemoveItemRow = (index) => {
    if (newGroup.items.length === 1) return;
    setNewGroup(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // 아이템 값 변경 이벤트 핸들러
  const handleItemChange = (index, field, value) => {
    setNewGroup(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index][field] = field === 'extra_price' || field === 'sort_order' ? Number(value) : value;
      return { ...prev, items: updatedItems };
    });
  };

  // [POST] 옵션 저장 프로세스
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newGroup.group_name.trim()) {
      alert('옵션 그룹명을 입력해주세요.');
      return;
    }

    // 빈 아이템 이름 필터링 유효성 검사
    const validItems = newGroup.items.filter(item => item.item_name.trim() !== '');
    if (validItems.length === 0) {
      alert('최소 1개 이상의 옵션 항목을 입력해야 합니다.');
      return;
    }

    try {
      const res = await axios.post(
        `/api/products/${productId}/options`,
        { ...newGroup, items: validItems },
        { withCredentials: true }
      );

      if (res.data && res.data.success) {
        alert('옵션이 등록되었습니다.');
        // 입력 폼 초기화
        setNewGroup({
          group_name: '',
          required: 'N',
          max_select: 1,
          sort_order: 0,
          items: [{ item_name: '', extra_price: 0, sort_order: 0 }]
        });
        // 목록 리로드
        fetchData();
      } else {
        alert(res.data?.message || '등록에 실패했습니다.');
      }
    } catch (err) {
      alert(err?.response?.data?.message || '등록 중 오류가 발생했습니다.');
    }
  };

  // [DELETE] 옵션 그룹 삭제 (ON DELETE CASCADE로 하위 아이템 연동 제거됨)
  const handleDeleteGroup = async (groupId) => {
    const ok = window.confirm('해당 옵션 그룹과 하위 아이템이 전부 삭제됩니다. 삭제하시겠습니까?');
    if (!ok) return;

    try {
      // Flask DDL 설계 스크립트 규격 반영 목적
      const res = await axios.delete(`/api/options/groups/${groupId}`, {
        withCredentials: true
      });

      if (res.data && res.data.success) {
        setOptionGroups((prev) => prev.filter((row) => row.group_id !== groupId));
        return;
      }
      alert(res.data?.message || '삭제에 실패했습니다.');
    } catch (err) {
      alert(err?.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loadingWrap}>
          <div>로딩중...</div>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.container}>
        
        {/* 상단 타이틀 영역 */}
        <div style={styles.titleRow}>
          <h2 style={styles.title}>상품 옵션 관리</h2>
          <button type="button" onClick={() => navigate(-1)} style={styles.backButton}>
            뒤로가기
          </button>
        </div>

        {/* 1. 옵션 등록 폼 구역 */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>새 옵션 그룹 추가</h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>옵션 그룹명</label>
                <input
                  type="text"
                  placeholder="예: 사이즈, 색상"
                  value={newGroup.group_name}
                  onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>필수 여부</label>
                <select
                  value={newGroup.required}
                  onChange={(e) => setNewGroup({ ...newGroup, required: e.target.value })}
                  style={styles.select}
                >
                  <option value="N">선택 옵션 (N)</option>
                  <option value="Y">필수 옵션 (Y)</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>최대 선택 개수</label>
                <input
                  type="number"
                  min="1"
                  value={newGroup.max_select}
                  onChange={(e) => setNewGroup({ ...newGroup, max_select: Number(e.target.value) })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>정렬 순서</label>
                <input
                  type="number"
                  value={newGroup.sort_order}
                  onChange={(e) => setNewGroup({ ...newGroup, sort_order: Number(e.target.value) })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* 하위 세부 아이템 작성 컴포넌트 섹션 */}
            <div style={styles.itemSection}>
              <div style={styles.itemSectionHeader}>
                <span style={styles.itemTitle}>세부 옵션 항목 (Option Items)</span>
                <button type="button" onClick={handleAddItemRow} style={styles.innerAddButton}>
                  + 항목 추가
                </button>
              </div>
              
              {newGroup.items.map((item, index) => (
                <div key={index} style={styles.itemRow}>
                  <input
                    type="text"
                    placeholder="옵션명 (예: XL, 블랙)"
                    value={item.item_name}
                    onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                    style={{ ...styles.input, flex: 2 }}
                  />
                  <input
                    type="number"
                    placeholder="추가 금액 (예: 1000)"
                    value={item.extra_price}
                    onChange={(e) => handleItemChange(index, 'extra_price', e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <input
                    type="number"
                    placeholder="정렬 순서"
                    value={item.sort_order}
                    onChange={(e) => handleItemChange(index, 'sort_order', e.target.value)}
                    style={{ ...styles.input, width: '80px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveItemRow(index)}
                    style={styles.innerDeleteButton}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'right', marginTop: '15px' }}>
              <button type="submit" style={styles.addButton}>옵션 그룹 저장하기</button>
            </div>
          </form>
        </div>

        {/* 2. 등록된 옵션 매핑 리스트 테이블 구역 */}
        <h3 style={{ ...styles.sectionTitle, marginTop: '40px' }}>현재 등록된 옵션 목록</h3>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>그룹명</th>
                <th style={styles.th}>필수여부</th>
                <th style={styles.th}>최대 선택</th>
                <th style={styles.th}>상세 아이템 목록 (추가금액)</th>
                <th style={styles.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {optionGroups.map((group) => (
                <tr key={group.group_id}>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{group.group_name}</td>
                  <td style={styles.td}>{group.required}</td>
                  <td style={styles.td}>{group.max_select}개</td>
                  <td style={{ ...styles.td, textAlign: 'left', paddingLeft: '20px' }}>
                    {group.items && group.items.length > 0 ? (
                      <div style={styles.tagWrap}>
                        {group.items.map((item) => (
                          <span key={item.item_id} style={styles.itemTag}>
                            {item.item_name} (+{item.extra_price.toLocaleString()}원)
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '13px' }}>등록된 하위 항목이 없습니다.</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      onClick={() => handleDeleteGroup(group.group_id)}
                      style={styles.deleteButton}
                    >
                      그룹 삭제
                    </button>
                  </td>
                </tr>
              ))}
              {optionGroups.length === 0 && (
                <tr>
                  <td style={styles.emptyTd} colSpan={5}>
                    등록된 상품 옵션이 없습니다. 위 폼에서 옵션을 구성해 보세요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SellerLayout>
  );
};

// 기존 제공해주신 가이드 스타일 컴포넌트 계열과 일치화된 테마 확장 구현
const styles = {
  loadingWrap: {
    minHeight: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  container: {
    padding: '0px',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  titleRow: {
    marginTop: '20px',
    marginBottom: '30px',
    borderBottom: '2px solid #dee2e6',
    paddingBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontWeight: 'bold',
  },
  backButton: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  formSection: {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
  },
  sectionTitle: {
    margin: '0 0 15px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
  },
  formRow: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: '1',
    minWidth: '150px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    background: '#fff',
  },
  itemSection: {
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '15px',
  },
  itemSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  itemTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#495057',
  },
  innerAddButton: {
    background: '#198754',
    color: '#fff',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  itemRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '8px',
    alignItems: 'center',
  },
  innerDeleteButton: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  addButton: {
    display: 'inline-block',
    background: '#0d6efd',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #dee2e6',
    background: '#fff',
  },
  th: {
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  td: {
    border: '1px solid #dee2e6',
    padding: '12px',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  emptyTd: {
    border: '1px solid #dee2e6',
    padding: '30px',
    textAlign: 'center',
    color: '#666',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  tagWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  itemTag: {
    background: '#e9ecef',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#495057',
    border: '1px solid #ced4da'
  }
};

export default SellerProductOption;